import { isTokenInvalid } from '@/helpers/jwtTokenHelper';
import {
  CreatePriceDropAlertPayload,
  PriceDropAlert,
  PriceDropAlertRequestStatus,
  PriceDropAlertStatus,
} from '@/store/types/price-drop-alert-state';
import { getRetryingAxiosInstance, StorageHelper } from '@smg-real-estate/hg-search-libs';

import { getAuthToken, getUserLoginInfo } from '@/helpers/Authentication/authClient';
import { StorageNamespace } from '@/helpers/storage-namespaces';
import { HgLocalStorageKey } from '@/models';
import { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

const storageHelper = new StorageHelper(StorageNamespace.searchAlerts);

const getPriceDropAlertToken = async (): Promise<string | null> => {
  const authToken = await getAuthToken();
  let priceDropAlertToken = storageHelper.getLocalStorageItem(HgLocalStorageKey.SEARCH_ALERT_TOKEN);

  const isInvalidToken = isTokenInvalid(priceDropAlertToken);
  if (isInvalidToken) {
    storageHelper.removeLocalStorageItem(HgLocalStorageKey.SEARCH_ALERT_TOKEN);
    priceDropAlertToken = null;
  }

  return priceDropAlertToken || authToken || null;
};

const getPriceDropAlertRequestsConfig = async (token: string) => {
  const user = await getUserLoginInfo();
  const priceDropAlertToken = storageHelper.getLocalStorageItem(
    HgLocalStorageKey.SEARCH_ALERT_TOKEN,
  );

  if (priceDropAlertToken && !isTokenInvalid(priceDropAlertToken)) {
    return {
      params: { email_token: token },
    };
  } else if (user) {
    return { headers: { Authorization: `Bearer ${token}` } };
  }

  return {};
};

const axiosInstance = getRetryingAxiosInstance({
  timeout: 4000,
  retryConfig: {
    retries: 3,
  },
});

export const usePriceDropAlertStore = defineStore('priceDropAlert', () => {
  const requestStatus = ref<PriceDropAlertRequestStatus>(PriceDropAlertRequestStatus.NOT_SUBMITTED);
  const priceDropAlert = ref<PriceDropAlert | undefined>(undefined);
  const dataFetchError = ref<AxiosError | undefined>(undefined);
  const isModalOpen = ref<boolean>(false);

  const isPriceDropAlertActive = computed(
    () => priceDropAlert.value?.status === PriceDropAlertStatus.ACTIVE,
  );
  const isRequestPending = computed(
    () => requestStatus.value === PriceDropAlertRequestStatus.PENDING,
  );
  const getIsModalOpen = computed(() => isModalOpen.value);

  const setPriceDropAlert = (newPriceDropAlert: PriceDropAlert | undefined) => {
    priceDropAlert.value = newPriceDropAlert;
  };
  const setPriceDropAlertStatus = (newStatus: PriceDropAlertRequestStatus) => {
    requestStatus.value = newStatus;
  };
  const setPriceDropAlertDataFetchError = (error: AxiosError | undefined) => {
    dataFetchError.value = error;
  };
  const setIsModalOpen = (isOpen: boolean) => {
    isModalOpen.value = isOpen;
  };

  const handlePriceDropAlertApiError = (error: AxiosError) => {
    setPriceDropAlertDataFetchError(error);

    if (error.response?.status === 404) {
      setPriceDropAlertStatus(PriceDropAlertRequestStatus.NOT_FOUND_ERROR);
      return;
    }

    if (error.response?.status === 409) {
      setPriceDropAlertStatus(PriceDropAlertRequestStatus.ALREADY_EXISTS_ERROR);
      return;
    }

    setPriceDropAlertStatus(PriceDropAlertRequestStatus.ERROR);
  };
  const createPriceDropAlert = async (payload: CreatePriceDropAlertPayload) => {
    setPriceDropAlertStatus(PriceDropAlertRequestStatus.PENDING);

    try {
      const response = await axiosInstance.post<PriceDropAlert>(
        `${import.meta.env.VUE_PRICE_DROP_ALERTS_API}`,
        payload,
      );

      setPriceDropAlert(response.data);
      setPriceDropAlertDataFetchError(undefined);
      setPriceDropAlertStatus(PriceDropAlertRequestStatus.CREATED);
      setIsModalOpen(false);
    } catch (error) {
      handlePriceDropAlertApiError(error as AxiosError);
    }
  };
  const activatePriceDropAlert = async (listingId: string) => {
    try {
      storageHelper.removeLocalStorageItem(HgLocalStorageKey.PRICE_DROP_ALERT_ACTION);

      const token = await getPriceDropAlertToken();

      if (!token) {
        setPriceDropAlertStatus(PriceDropAlertRequestStatus.NO_TOKEN_ERROR);
        return;
      }

      const config = await getPriceDropAlertRequestsConfig(token);

      setPriceDropAlertStatus(PriceDropAlertRequestStatus.PENDING);

      const { data: activatedPriceDropAlert } = await axiosInstance.put<PriceDropAlert>(
        `${import.meta.env.VUE_PRICE_DROP_ALERTS_API}/${listingId}/activate`,
        undefined,
        config,
      );

      setPriceDropAlert(activatedPriceDropAlert);
      setPriceDropAlertStatus(PriceDropAlertRequestStatus.ACTIVATED);
      setPriceDropAlertDataFetchError(undefined);
    } catch (error) {
      // Handle conflict error when alert is already activated
      if (error.response?.status === 409) {
        await getPriceDropAlert(listingId);
        return;
      }

      handlePriceDropAlertApiError(error as AxiosError);
    }
  };
  const getPriceDropAlert = async (listingId: string) => {
    try {
      const token = await getPriceDropAlertToken();

      if (!token) {
        setPriceDropAlertStatus(PriceDropAlertRequestStatus.NO_TOKEN_ERROR);
        return;
      }

      const config = await getPriceDropAlertRequestsConfig(token);

      const { data: fetchedPriceDropAlert } = await axiosInstance.get(
        `${import.meta.env.VUE_PRICE_DROP_ALERTS_API}/${listingId}`,
        config,
      );

      setPriceDropAlert(fetchedPriceDropAlert);
      setPriceDropAlertDataFetchError(undefined);
      setPriceDropAlertStatus(PriceDropAlertRequestStatus.FETCHED);
    } catch (error) {
      handlePriceDropAlertApiError(error as AxiosError);
    }
  };
  const deletePriceDropAlert = async (listingId: string) => {
    try {
      storageHelper.removeLocalStorageItem(HgLocalStorageKey.PRICE_DROP_ALERT_ACTION);

      const token = await getPriceDropAlertToken();

      if (!token) {
        setPriceDropAlertStatus(PriceDropAlertRequestStatus.NO_TOKEN_ERROR);
        return;
      }

      const config = await getPriceDropAlertRequestsConfig(token);

      setPriceDropAlertStatus(PriceDropAlertRequestStatus.PENDING);
      await axiosInstance.delete<PriceDropAlert>(
        `${import.meta.env.VUE_PRICE_DROP_ALERTS_API}/${listingId}`,
        config,
      );

      setPriceDropAlert(undefined);
      setPriceDropAlertStatus(PriceDropAlertRequestStatus.DELETED);
      setPriceDropAlertDataFetchError(undefined);
    } catch (error) {
      handlePriceDropAlertApiError(error as AxiosError);
    }
  };

  return {
    // state
    requestStatus,
    priceDropAlert,
    dataFetchError,
    isModalOpen,

    // getters
    isPriceDropAlertActive,
    isRequestPending,
    getIsModalOpen,

    // mutations
    setPriceDropAlertStatus,
    setIsModalOpen,

    // actions
    createPriceDropAlert,
    activatePriceDropAlert,
    getPriceDropAlert,
    deletePriceDropAlert,
  };
});
